import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  IconAlarm,
  IconAlertTriangle,
  IconCoffee,
  IconDeviceMobileOff,
  IconHelp,
  IconLock,
} from '@tabler/icons-react';
import { CircularProgress, Link, Snackbar, Typography } from '@mui/material';
import { AlertColor } from '@mui/material/Alert';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { OverlayScrollbars } from 'overlayscrollbars';
import type { OverlayScrollbarsComponentRef } from 'overlayscrollbars-react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Capability, hasAnyCapability } from '@repo/utils/auth';
import type { ThemeMode } from '../../theme';
import { DASHBOARD_HREF, SIGN_IN_HREF, withRedirect } from '../../utils/common/hrefs';
import useCapabilities from '../../utils/react/hooks/useCapabilities';
import useIsSignedIn from '../../utils/react/hooks/useIsSignedIn';
import { useThemeModeOverride } from '../AppThemeProvider';
import Button from '../Button';
import ErrorBoundary from '../ErrorBoundary';

import { SidePanelProvider } from '../SidePanel';
import AppBar from './AppBar';
import BottomNav from './BottomNav';
import Drawer from './Drawer';
import {
  ContentContainer,
  ContentPad,
  ContentScroll,
  ContentScrollNative,
  GutterCap,
  IconBorder,
  IconContainer,
  LoadingContainer,
  Main,
  MessageContainer,
  MessageContent,
  MessageTitle,
  Nav,
  SnackbarAlert,
} from './index.styles';
import { SnackbarContext } from './SnackbarContext';
import { getPageTitle } from './utils';

interface PageMessageProps {
  content?: string | React.ReactElement | undefined;
  title: string;
  icon: React.ReactElement;
  actionLabel?: string;
  actionHref?: string;
  containerProps?: React.HTMLAttributes<HTMLDivElement>;
}

export const PageMessage = ({
  content,
  title,
  icon,
  actionLabel,
  actionHref,
  containerProps,
}: PageMessageProps) => (
  <MessageContainer {...containerProps}>
    <IconBorder>
      <IconContainer>{icon}</IconContainer>
    </IconBorder>
    <MessageTitle variant='h5'>{title}</MessageTitle>
    {/* A div, as messages pass paragraphs of their own */}
    <MessageContent variant='body2' component='div'>
      {content}
    </MessageContent>
    {actionLabel && actionHref && (
      <Button size='medium' href={actionHref}>
        {actionLabel}
      </Button>
    )}
  </MessageContainer>
);

/** Where a message's one button leads: the dashboard, unless a page has somewhere better. */
interface MessageActionProps {
  actionLabel?: string;
  actionHref?: string;
}

const withDashboardFallback = ({
  actionLabel = 'Return to dashboard',
  actionHref = DASHBOARD_HREF,
}: MessageActionProps) => ({ actionLabel, actionHref });

export const NoPermissionMessage = (action: MessageActionProps) => (
  <PageMessage
    title='No access'
    content={
      <>
        <Typography>You do not have access to this content.</Typography>
        <br />
        <Typography>If you believe this is a mistake, please let us know.</Typography>
      </>
    }
    icon={<IconLock size={32} />}
    {...withDashboardFallback(action)}
  />
);

export const NotOnMobileMessage = () => (
  <PageMessage
    title='Not available on mobile'
    content={
      <>
        <Typography>This content is not available on mobile.</Typography>
        <br />
        <Typography>Please view the content on desktop or tablet.</Typography>
      </>
    }
    icon={<IconDeviceMobileOff size={32} />}
    actionLabel='Return to dashboard'
    actionHref={DASHBOARD_HREF}
  />
);

interface ComingSoonMessageProps {
  dueDate?: string;
}

export const ComingSoonMessage = ({ dueDate }: ComingSoonMessageProps) => (
  <PageMessage
    title='Coming soon'
    content={
      <>
        <Typography>
          {dueDate
            ? `This feature will be available in ${dueDate}.`
            : 'This feature is not available yet.'}
        </Typography>
        <br />
        <Typography>
          Follow{' '}
          <Link href='https://www.instagram.com/cognito.tuition/' target='_blank' rel='noreferrer'>
            @cognito.tuition
          </Link>{' '}
          for updates!
        </Typography>
      </>
    }
    icon={<IconAlarm size={32} />}
    actionLabel='Return to dashboard'
    actionHref={DASHBOARD_HREF}
  />
);

export const NoContentMessage = () => (
  <PageMessage
    title='Nothing here yet'
    content={
      <>
        <Typography>There is no content available to you here yet.</Typography>
        <br />
        <Typography>Check back again soon!</Typography>
      </>
    }
    icon={<IconHelp size={32} />}
    actionLabel='Return to dashboard'
    actionHref={DASHBOARD_HREF}
  />
);

export const AllFinishedMessage = () => (
  <PageMessage
    title='All finished!'
    content={
      <>
        <Typography>You&apos;ve finished everything here!</Typography>
        <br />
        <Typography>Pour yourself a nice cup of tea for a job well done.</Typography>
      </>
    }
    icon={<IconCoffee size={32} />}
    actionLabel='Return to dashboard'
    actionHref={DASHBOARD_HREF}
  />
);

export const SomethingWentWrongMessage = () => (
  <PageMessage
    title='Something went wrong'
    content={
      <>
        <Typography>An unexpected error occurred while displaying this page.</Typography>
        <br />
        <Typography>Try again, or return to the dashboard.</Typography>
      </>
    }
    icon={<IconHelp size={32} />}
    actionLabel='Return to dashboard'
    actionHref={DASHBOARD_HREF}
  />
);

export const NotFoundMessage = (action: MessageActionProps) => (
  <PageMessage
    title='Nothing here! (404)'
    content={
      <>
        <Typography>The page you are looking for doesn&apos;t exist or has been moved.</Typography>
        <br />
        <Typography>Go back to navigate to what you&apos;re looking for!</Typography>
      </>
    }
    icon={<IconHelp size={32} />}
    {...withDashboardFallback(action)}
  />
);

export const ErrorMessage = (action: MessageActionProps) => (
  <PageMessage
    title='Something went wrong'
    content={
      <>
        <Typography>This content could not be loaded.</Typography>
        <br />
        <Typography>Please try again, or let us know if the problem persists.</Typography>
      </>
    }
    icon={<IconAlertTriangle size={32} />}
    {...withDashboardFallback(action)}
  />
);

interface QueryErrorMessageProps extends MessageActionProps {
  /** Error returned by an RTK Query hook */
  error?: unknown;
}

/** Page message for a failed query's HTTP status: 403 -> NoPermissionMessage, 404 -> NotFoundMessage, else ErrorMessage. */
export const QueryErrorMessage = ({ error, ...action }: QueryErrorMessageProps) => {
  const status =
    typeof error === 'object' && error !== null && 'status' in error
      ? (error as { status: unknown }).status
      : undefined;

  if (status === 403) {
    return <NoPermissionMessage {...action} />;
  }

  if (status === 404) {
    return <NotFoundMessage {...action} />;
  }

  return <ErrorMessage {...action} />;
};

interface LoadingWrapperProps {
  children?: React.ReactElement | React.ReactElement[];
  decorate?: boolean;
  loading: boolean;
}

export const LoadingWrapper = ({ children, loading, decorate = false }: LoadingWrapperProps) =>
  loading ? (
    <LoadingContainer decorate={decorate}>
      <CircularProgress />
    </LoadingContainer>
  ) : (
    // react/jsx-no-useless-fragment is disabled here as LoadingWrapper must return a single element
    // and `children` may be an array of elements

    <>{children}</>
  );

interface SnackbarProviderProps {
  children: React.ReactElement | React.ReactElement[];
}

export const SnackbarProvider = ({ children }: SnackbarProviderProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState<string>('');
  const [type, setType] = useState<AlertColor>('info');

  const closeSnackbar = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setIsOpen(false);
  };

  const createSnackbar = useCallback((newMessage: string, newType?: AlertColor) => {
    setMessage(newMessage);
    setType(newType || 'info');
    setIsOpen(true);
  }, []);

  return (
    <SnackbarContext.Provider value={createSnackbar}>
      {children}
      <Snackbar
        open={isOpen}
        autoHideDuration={3000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <SnackbarAlert onClose={closeSnackbar} severity={type}>
          {message}
        </SnackbarAlert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
};

interface ContentProps {
  children: React.ReactElement | React.ReactElement[];
  decorate: boolean;
  loading: boolean;
  capabilities?: readonly Capability[];
  hideDrawer?: boolean;
  hidePadding?: boolean;
}

const Content = ({
  children,
  decorate,
  loading,
  capabilities,
  hideDrawer = false,
  hidePadding = false,
}: ContentProps) => {
  const theme = useTheme();
  const location = useLocation();

  // Below `sm` we swap the drawer/rail for the mobile bottom nav.
  const isSm = useMediaQuery(theme.breakpoints.up('sm'));

  const [drawerOpen, setDrawerOpen] = useState(isSm);

  // Main scroll container when `hideDrawer` is set; persists across routes, so reset on nav.
  const mainRef = useRef<HTMLElement>(null);
  // Tablet/desktop overlay-scrollbar viewport for normal pages; reset on nav.
  const scrollRef = useRef<OverlayScrollbarsComponentRef>(null);
  // Mobile native scroll container for normal pages; reset on nav.
  const nativeScrollRef = useRef<HTMLDivElement>(null);

  // Setting the same value again does not re-render, so only leaving the top costs one.
  const [isScrolled, setIsScrolled] = useState(false);
  const trackScroll = useCallback(
    (element?: HTMLElement | null) => setIsScrolled((element?.scrollTop ?? 0) > 0),
    []
  );
  const scrollEvents = useMemo(
    () => ({ scroll: (instance: OverlayScrollbars) => trackScroll(instance.elements().viewport) }),
    [trackScroll]
  );

  const closeDrawer = () => setDrawerOpen(false);
  const openDrawer = () => setDrawerOpen(true);
  const toggleDrawer = () => setDrawerOpen(!drawerOpen);

  useEffect(() => {
    // The breakpoint swaps the scroll container, and the new one starts at the top
    setIsScrolled(false);
    if (!isSm) {
      closeDrawer();
    } else {
      openDrawer();
    }
  }, [isSm]);

  useEffect(() => {
    scrollRef.current?.osInstance()?.elements().viewport.scrollTo({ top: 0, left: 0 });
    nativeScrollRef.current?.scrollTo({ top: 0, left: 0 });
    mainRef.current?.scrollTo({ top: 0, left: 0 });
    setIsScrolled(false);
  }, [location.pathname]);

  // Boundary sits below the drawer and app bar so a lazy page chunk suspends
  // only the content area — one above the shell hides the whole window until
  // the chunk lands. Its fallback matches the auth gate's, so both stages of a
  // first load paint identically.
  const decoratedContent = (
    <LoadingWrapper loading={loading} decorate={decorate}>
      <Suspense fallback={<LoadingWrapper loading decorate={decorate} />}>{children}</Suspense>
    </LoadingWrapper>
  );

  const paddedContent = (
    <ContentPad $isSm={isSm} $hidePadding={hidePadding}>
      <GutterCap aria-hidden='true' $painted={isScrolled} />
      {decoratedContent}
    </ContentPad>
  );

  return decorate ? (
    <ContentContainer>
      {!hideDrawer && isSm && (
        <Nav as='nav'>
          <Drawer
            open={isSm || drawerOpen}
            expanded={drawerOpen}
            onToggle={toggleDrawer}
            capabilities={capabilities}
          />
        </Nav>
      )}
      <Main
        ref={mainRef}
        as='main'
        $expanded={hideDrawer ? false : drawerOpen}
        $isSm={isSm}
        $hideAppbar={hideDrawer}
        $hidePadding={hidePadding}
      >
        {!hideDrawer && isSm && <AppBar />}
        {hideDrawer ? (
          decoratedContent
        ) : (
          <SidePanelProvider>
            {isSm ? (
              <ContentScroll ref={scrollRef} events={scrollEvents}>
                {paddedContent}
              </ContentScroll>
            ) : (
              <ContentScrollNative
                ref={nativeScrollRef}
                onScroll={event => trackScroll(event.currentTarget)}
              >
                {paddedContent}
              </ContentScrollNative>
            )}
          </SidePanelProvider>
        )}
        {!hideDrawer && !isSm && <BottomNav capabilities={capabilities} />}
      </Main>
    </ContentContainer>
  ) : (
    decoratedContent
  );
};

interface PageLayoutProps {
  children: React.ReactElement | React.ReactElement[];
  /**
   * Page title
   */
  title: string;
  /**
   * Redirect paths
   */
  redirects?:
    | {
        onAuthRedirect: string;
        onAuthlessRedirect?: undefined;
        onUnauthorisedRedirect?: string;
      }
    | {
        onAuthRedirect?: undefined;
        onAuthlessRedirect: string;
        onUnauthorisedRedirect?: string;
      };
  /**
   * The page remaing in loading mode *at least* while this prop is `true`
   */
  loading?: boolean;
  /**
   * If `true`, page decorations such as the AppBar are rendered in
   */
  decorate?: boolean;
  /**
   * If `true`, all redirects are cancelled
   */
  disableRedirect?: boolean;
  /**
   * Redirects a reader holding none of these capabilities
   */
  authorisedCapabilities?: Capability[];
  /**
   * If `true`, the app bar and drawer is hidden
   */
  hideDrawer?: boolean;
  /**
   * If `true`, the padding around the page content is removed
   */
  hidePadding?: boolean;
  /**
   * The mode the whole app is drawn in while the page is open, whatever the
   * user's setting; absent follows the user's own
   */
  themeMode?: ThemeMode;
}

export const PageLayout = ({
  children,
  redirects,
  loading: isPageLoading = false,
  decorate = false,
  disableRedirect = false,
  title,
  authorisedCapabilities,
  hideDrawer,
  hidePadding,
  themeMode,
}: PageLayoutProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const capabilities = useCapabilities();
  useThemeModeOverride(themeMode);

  const isAuthenticated = useIsSignedIn();
  const [isAuthorised, setIsAuthorised] = useState<boolean | null>(null);
  const [userCapabilities, setUserCapabilities] = useState<Capability[] | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = getPageTitle(title);
  }, [title]);

  // Stop loading and take the reader's capabilities once the authentication state is fetched
  useEffect(() => {
    if (isAuthenticated !== null && !isPageLoading) {
      setLoading(false);
      if (isAuthenticated) {
        setUserCapabilities(capabilities);
      }
    }
  }, [isAuthenticated, capabilities, isPageLoading]);

  useEffect(() => {
    // A page naming no capabilities is open to every reader
    const isNoAuthRequired = authorisedCapabilities === undefined;
    const holdsAuthorisedCapability =
      authorisedCapabilities &&
      userCapabilities &&
      hasAnyCapability(userCapabilities, authorisedCapabilities);
    const isUserAuthorised = isNoAuthRequired || holdsAuthorisedCapability;

    // Null while the capabilities are still being read, so the page renders and
    // the redirect waits until they are known
    setIsAuthorised(isUserAuthorised ?? null);
  }, [userCapabilities, authorisedCapabilities]);

  useEffect(() => {
    // AWS S3 static website hosting does not support redirects, so we need to
    // manually redirect the user if they have a hash in their URL
    const path = (/#!(\/.*)$/.exec(location.hash) || [])[1];
    if (path) {
      navigate(path, { replace: true });
    }
  }, [location, navigate]);

  // Perform redirects based on authentication / authorisation state
  useEffect(() => {
    const redirectOnAuth = !!redirects?.onAuthRedirect && isAuthenticated === true;
    const redirectOnAuthless = !!redirects?.onAuthlessRedirect && isAuthenticated === false;

    if (!disableRedirect) {
      if (redirectOnAuth) {
        navigate(redirects?.onAuthRedirect, { replace: true });
      }
      // If not authenticated, redirect to the sign in page with the current path as a
      // redirect parameter. Read off the browser rather than the router: a page
      // rendered against a location of its own reports that one, not the URL.
      if (redirectOnAuthless) {
        navigate(withRedirect(redirects.onAuthlessRedirect, window.location.pathname), {
          replace: true,
        });
      }
      if (isAuthorised === false) {
        navigate(redirects?.onUnauthorisedRedirect || DASHBOARD_HREF, { replace: true });
      }
    }
  }, [disableRedirect, isAuthorised, isAuthenticated, redirects, location.pathname, navigate]);

  if (isAuthenticated === null) {
    return <LoadingWrapper loading />;
  }

  return (
    <SnackbarProvider>
      <Content
        decorate={decorate}
        loading={loading}
        capabilities={userCapabilities}
        hideDrawer={hideDrawer}
        hidePadding={hidePadding}
      >
        {/* resetKeys clears a tripped boundary on same-path navigation (?tab=/?section=)
            without remounting the subtree; the key still remounts on path change */}
        <ErrorBoundary
          key={location.pathname}
          resetKeys={[location.pathname, location.search]}
          fallback={<SomethingWentWrongMessage />}
        >
          {children}
        </ErrorBoundary>
      </Content>
    </SnackbarProvider>
  );
};

interface InheritedPageLayoutProps {
  children: React.ReactElement | React.ReactElement[];
  title: string;
  authorisedCapabilities?: Capability[];
  hideDrawer?: boolean;
  hidePadding?: boolean;
  themeMode?: ThemeMode;
}

export const AuthPageLayout = ({ children, ...props }: InheritedPageLayoutProps) => (
  <PageLayout redirects={{ onAuthRedirect: DASHBOARD_HREF }} {...props}>
    {children}
  </PageLayout>
);

export const ProtectedPageLayout = ({ children, ...props }: InheritedPageLayoutProps) => (
  <PageLayout redirects={{ onAuthlessRedirect: SIGN_IN_HREF }} decorate {...props}>
    {children}
  </PageLayout>
);
